import type { ShadowOrganization } from "@sitescene/types";
import type { PropsWithChildren } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";

import { apiClient } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import { setSelectedOrgGetter } from "@/api/org-manager";
import { queryKeys } from "@/api/query-keys";
import { retryUnlessPermanent } from "@/api/retry";
import { useAuth } from "@/providers/auth-provider";

import { ORG_STORAGE_KEY, OrgSelectionContext } from "./org-selection-context";

/**
 * Loads the organizations this user may configure and tracks which one is
 * active.
 *
 * The selection is a client convenience only — it rides along as the
 * `X-Shadow-Org` header and the backend re-validates it against the caller's
 * ShadowAccess record on every request, so a tampered value gains nothing.
 */
export const OrgSelectionProvider = ({ children }: PropsWithChildren) => {
	const { isAuthenticated, isLoadingAuth } = useAuth();

	const [selectedOrgCode, setSelectedOrgCode] = useState<null | string>(() => {
		try {
			return localStorage.getItem(ORG_STORAGE_KEY);
		} catch {
			// Private browsing or blocked site data — fall back to no selection.
			return null;
		}
	});

	// This provider sits above the router's auth guard, so without `enabled` the
	// query would fire before a token exists — including mid-callback from Kinde.
	// A 401 makes the API client call `triggerLogout()`, so an unauthenticated
	// visitor would be bounced through logout instead of reaching the sign-in page.
	const isReady = isAuthenticated && !isLoadingAuth;

	const { data, error, isLoading } = useQuery({
		enabled: isReady,
		queryFn: async () => {
			const response = await apiClient.get<ShadowOrganization[]>(
				ENDPOINTS.shadow.organizations,
			);
			return response.data;
		},
		queryKey: queryKeys.organizations,
		retry: retryUnlessPermanent,
	});

	const organizations = useMemo(() => data ?? [], [data]);

	// Fall back to the first organization when nothing is stored, or when the
	// stored one has since been revoked.
	const selected = useMemo(
		() =>
			organizations.find((org) => org.orgCode === selectedOrgCode) ??
			organizations[0] ??
			null,
		[organizations, selectedOrgCode],
	);

	// Let the axios interceptor read the current selection without prop drilling.
	useEffect(() => {
		setSelectedOrgGetter(() => selected?.orgCode ?? null);
	}, [selected]);

	const selectOrg = useCallback((orgCode: string) => {
		setSelectedOrgCode(orgCode);
		try {
			localStorage.setItem(ORG_STORAGE_KEY, orgCode);
		} catch {
			// Persistence is a convenience; the in-memory selection still works.
		}
	}, []);

	const value = useMemo(
		() => ({
			error: error instanceof Error ? error : null,
			// Stay in the loading state until auth settles, so the shell never
			// briefly renders "no organizations assigned" before the query runs.
			isLoading: !isReady || isLoading,
			organizations,
			selected,
			selectOrg,
		}),
		[error, isLoading, isReady, organizations, selected, selectOrg],
	);

	return <OrgSelectionContext value={value}>{children}</OrgSelectionContext>;
};
